import { ComponentPropsWithoutRef } from 'react'
import * as ProgressPrimitive from '@radix-ui/react-progress'

import ProgressBar from './progress-bar'

export interface Step {
  /** Display name of the step */
  name: string
  /** Current status of the step
   * - 'complete': Step has been finished
   * - 'current': Step is currently active
   * - 'upcoming': Step hasn't been reached yet
   */
  status: 'complete' | 'current' | 'upcoming'
}

export interface ProgressProps
  extends ComponentPropsWithoutRef<typeof ProgressPrimitive.Root> {
  /** Array of steps to display in the progress bar.
   * If not provided, renders a simple progress bar.
   * @example
   * ```tsx
   * steps={[
   *   { name: "Details", status: "complete" },
   *   { name: "Address", status: "current" },
   *   { name: "Payment", status: "upcoming" }
   * ]}
   * ```
   */
  steps?: Step[]
  /** Progress value between 0 and 100.
   * Required when steps are not provided.
   * When used with steps, overrides the automatically calculated progress.
   * @default 0
   */
  progress?: number
  /** Variant of the progress bar
   * - 'bullets': Display only step indicators
   * - 'bar': Display only progress bar
   * - 'both': Display both step indicators and progress bar
   */
  variant?: 'bullets' | 'bar' | 'both'
  /** Controls the ping animation on the current step indicator.
   * Only applies when steps are provided.
   * @default true
   */
  animated?: boolean
  /** Shows step names below the progress bar.
   * Only applies when steps are provided and variant includes 'bar'.
   * @default false
   */
  displayStepNames?: boolean
  /** Additional class name */
  className?: string
}

export default ProgressBar
export type { ProgressBarRef } from './progress-bar'
